import { Link } from 'react-router-dom';
import { PlayerProfile } from '../PlayerProfile';
import { cn } from '../../utils/cn';
import { actorMeta, isPlayerUuid } from '../../utils/logbook';
import type { LogbookActorKind } from '../../types/logbook';

interface LogbookActorProps {
  actorId: string;
  actorName: string;
  actorKind: LogbookActorKind;
  /** Renders the name only, for dense rows. */
  inline?: boolean;
  className?: string;
}

/**
 * Who a record is attributed to.
 *
 * A player UUID becomes the ordinary Argus player profile, so a name, a head and the route to their
 * full history are the same everywhere in the panel. Anything else is drawn as what it is: a fake
 * player driven by a machine, an automation, or an unattributed capture where no initiator reached
 * the hook at all. That last case is common and deliberate in Logbook, and treating it as a person
 * would be the single easiest way to accuse the wrong player.
 */
export const LogbookActor = ({ actorId, actorName, actorKind, inline = true, className }: LogbookActorProps) => {
  const meta = actorMeta(actorKind);
  const Icon = meta.icon;

  if (actorKind === 'player' && isPlayerUuid(actorId)) {
    return (
      <span className={cn('min-w-0', className)}>
        <PlayerProfile uuid={actorId} name={actorName || undefined} inline={inline} showUuid={!inline} />
      </span>
    );
  }

  const label = actorName || (isPlayerUuid(actorId) ? actorId : actorId) || meta.label;
  const body = (
    <span
      className={cn('inline-flex items-center gap-1.5 min-w-0 align-middle', className)}
      title={`${meta.label}: ${meta.meaning}`}
    >
      <span className="shrink-0 inline-flex" style={{ color: `var(--tone-${meta.tone}-text)` }}>
        <Icon className="w-3.5 h-3.5" />
      </span>
      <span className={cn('truncate', actorKind === 'unknown' ? 'text-glass-400 italic' : 'text-white')}>
        {actorKind === 'unknown' && !actorName ? 'Unattributed' : label}
      </span>
    </span>
  );

  // A fake player still carries a UUID often enough to be worth linking when it looks like one.
  return isPlayerUuid(actorId) ? <Link to={`/player/${actorId}`} className="min-w-0 hover:underline">{body}</Link> : body;
};
